/* eslint-disable react-hooks/exhaustive-deps */
// src/components/AccountingEntriesGridMaterial.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Paper,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Popover,
  IconButton,
  Snackbar,
  Button,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import axios from 'axios';

export const AccountingEntriesGridMaterial = () => {
  // Grid state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Column filters state
  const [columnFilters, setColumnFilters] = useState({});
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);

  // Sorting state
  const [orderBy, setOrderBy] = useState('OID');
  const [order, setOrder] = useState('desc');

  // UI state
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState(null);

  // Refs
  const abortControllerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isLoadingRef = useRef(false);

  // Column definitions - UI in Spanish, code in English
  const [columns] = useState([
    { name: 'OID', title: 'ID', type: 'number', width: 80 },
    { name: 'DesEmpresa', title: 'Empresa', type: 'string', width: 180 },
    { name: 'DesPeriodoContable', title: 'Período Contable', type: 'string', width: 150 },
    { name: 'Voucher', title: 'Voucher', type: 'string', width: 120 },
    { name: 'DesProyecto', title: 'Proyecto', type: 'string', width: 200 },
    { name: 'DesCentroCosto', title: 'Centro Costo', type: 'string', width: 200 },
    { name: 'Cuenta', title: 'Cuenta Contable', type: 'string', width: 150 },
    { name: 'DesInsumo', title: 'Descripción Insumo', type: 'string', width: 300 },
    { name: 'SocioNegocio', title: 'Socio Negocio', type: 'string', width: 200 },
    { name: 'FechaTransaccion', title: 'Fecha Transacción', type: 'date', width: 120 },
    { name: 'Debito', title: 'Débito', type: 'currency', width: 120 },
    { name: 'Credito', title: 'Crédito', type: 'currency', width: 120 },
    { name: 'Saldo', title: 'Saldo', type: 'currency', width: 120 },
    { name: 'TipoMovimiento', title: 'Tipo Movimiento', type: 'string', width: 120 },
    { name: 'Observacion', title: 'Observación', type: 'string', width: 200 },
    { name: 'Almacen', title: 'Almacén', type: 'string', width: 150 },
    { name: 'CodProyecto', title: 'Código Proyecto', type: 'string', width: 120 },
    { name: 'CreacionUsuarioAC', title: 'Creado Por', type: 'string', width: 120 },
    { name: 'CreacionFechaAC', title: 'Fecha Creación', type: 'date', width: 150 },
    { name: 'Procedencia', title: 'Procedencia', type: 'string', width: 200 },
    { name: 'TipoAsiento', title: 'Tipo Asiento', type: 'string', width: 120 },
    { name: 'Linea', title: 'Línea', type: 'number', width: 80 },
    { name: 'DesCuentaGenerica', title: 'Cuenta Genérica', type: 'string', width: 150 },
    { name: 'RUCSocioNegocio', title: 'RUC', type: 'string', width: 120 },
    { name: 'FechaAplicacionContable', title: 'Fecha Aplicación', type: 'date', width: 120 },
  ]);

  const PAGE_SIZE = 50;
  const SCROLL_THRESHOLD = 300;

  // Helper: format value
  const formatValue = (value, type) => {
    if (value === null || value === undefined || value === '') return '—';

    switch (type) {
      case 'currency':
        return value.toLocaleString('es-PE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      case 'date':
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('es-PE');
        }
        return value;
      default:
        return value;
    }
  };

  // Build API filters
  const buildApiFilters = useCallback((columnFiltersObj) => {
    let filters = [];

    Object.entries(columnFiltersObj).forEach(([columnName, filterValue]) => {
      if (filterValue && filterValue.trim()) {
        const columnFilter = [columnName, 'contains', filterValue];
        if (filters.length > 0) {
          filters = ['and', filters, columnFilter];
        } else {
          filters = columnFilter;
        }
      }
    });

    return filters;
  }, []);

  // Load data from API
  const loadData = useCallback(async (skip, isLoadMore = false) => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    isLoadingRef.current = true;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const apiFilters = buildApiFilters(columnFilters);

      const params = new URLSearchParams();
      params.append('skip', skip);
      params.append('take', PAGE_SIZE);
      params.append('requireTotalCount', 'true');
      params.append('sort', JSON.stringify([{ selector: orderBy, desc: order === 'desc' }]));

      if (apiFilters.length > 0) {
        params.append('filter', JSON.stringify(apiFilters));
      }

      const response = await axios.get(
        'https://s10cloud.com:50023/CentralPrueba/api/AsientoContableApi/SearchAsientoContableQuery',
        { params, signal: abortController.signal }
      );

      let data = [];
      let total = 0;

      if (Array.isArray(response.data)) {
        data = response.data;
        total = response.data.length;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
        total = response.data.totalCount || response.data.data.length;
      } else if (response.data?.result && Array.isArray(response.data.result)) {
        data = response.data.result;
        total = response.data.totalCount || response.data.result.length;
      }

      if (isLoadMore) {
        setRows(prev => [...prev, ...data]);
      } else {
        setRows(data);
      }

      setTotalCount(total);
      setCurrentSkip(skip + data.length);
      setHasMore(skip + data.length < total);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error:', err);
        setSnackbarMessage('Error al cargar los datos');
        if (!isLoadMore) {
          setRows([]);
          setTotalCount(0);
        }
      }
    } finally {
      isLoadingRef.current = false;
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [columnFilters, orderBy, order, buildApiFilters]);

  // Initial load
  useEffect(() => {
    const initialLoad = async () => {
      setIsFirstLoad(true);
      await loadData(0, false);
      setIsFirstLoad(false);
    };
    initialLoad();
  }, []);

  // Reload when sorting or column filters change
  useEffect(() => {
    if (!isFirstLoad) {
      setRows([]);
      setCurrentSkip(0);
      setHasMore(true);
      loadData(0, false);
    }
  }, [orderBy, order, columnFilters]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceToBottom < SCROLL_THRESHOLD && !loadingMore && hasMore && !loading && !isLoadingRef.current) {
      loadData(currentSkip, true);
    }
  }, [loadingMore, hasMore, loading, currentSkip, loadData]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      let timeoutId;
      const throttledScroll = () => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(handleScroll, 100);
      };
      container.addEventListener('scroll', throttledScroll);
      return () => {
        container.removeEventListener('scroll', throttledScroll);
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [handleScroll]);

  // Sorting handler
  const handleSort = (columnName) => {
    const isAsc = orderBy === columnName && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnName);
  };

  // Column filter handlers
  const handleColumnFilterClick = (event, columnName) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumn(columnName);
  };

  const handleColumnFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleColumnFilterChange = (value) => {
    setColumnFilters(prev => ({
      ...prev,
      [activeFilterColumn]: value
    }));
  };

  const clearColumnFilter = (columnName) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnName];
      return newFilters;
    });
  };

  return (
    <Box>
      <Paper>
        <TableContainer
          ref={scrollContainerRef}
          sx={{ height: '70vh', overflow: 'auto' }}
        >
          {loading && rows.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
              <Typography variant="body2" ml={2}>Cargando datos...</Typography>
            </Box>
          ) : (
            <>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.name}
                        sx={{
                          width: column.width,
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TableSortLabel
                            active={orderBy === column.name}
                            direction={orderBy === column.name ? order : 'asc'}
                            onClick={() => handleSort(column.name)}
                          >
                            {column.title}
                          </TableSortLabel>
                          <IconButton
                            size="small"
                            onClick={(e) => handleColumnFilterClick(e, column.name)}
                            sx={{ p: 0.5 }}
                          >
                            <FilterIcon fontSize="small" />
                          </IconButton>
                          {columnFilters[column.name] && (
                            <ClearIcon
                              fontSize="small"
                              sx={{ cursor: 'pointer', color: 'action.active' }}
                              onClick={() => clearColumnFilter(column.name)}
                            />
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.OID} hover>
                      {columns.map((column) => (
                        <TableCell key={column.name}>
                          <Typography variant="body2">
                            {formatValue(row[column.name], column.type)}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {loadingMore && (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={30} />
                  <Typography variant="body2" ml={2}>Cargando más registros...</Typography>
                </Box>
              )}

              {!hasMore && rows.length > 0 && (
                <Box display="flex" justifyContent="center" p={2}>
                  <Typography variant="body2" color="textSecondary">
                    ✓ No hay más registros
                  </Typography>
                </Box>
              )}

              {rows.length === 0 && !loading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" flexDirection="column">
                  <Typography variant="h6" color="textSecondary">No hay datos para mostrar</Typography>
                  <Typography variant="body2" color="textSecondary">Intenta con otros filtros</Typography>
                </Box>
              )}
            </>
          )}
        </TableContainer>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="textSecondary">
            Mostrando {rows.length.toLocaleString()} de {totalCount.toLocaleString()} registros
          </Typography>
        </Box>
      </Paper>

      {/* Column filter popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleColumnFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filtrar por {columns.find(c => c.name === activeFilterColumn)?.title}
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={columnFilters[activeFilterColumn] || ''}
            onChange={(e) => handleColumnFilterChange(e.target.value)}
            placeholder="Ingrese valor para filtrar..."
            autoFocus
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleColumnFilterClose}>
              Cerrar
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Snackbar for messages */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarMessage(null)} severity="info">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};